// src/hooks/useCompanies.js
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function getAuditHeaders() {
    try {
        const raw = localStorage.getItem("authUser") || localStorage.getItem("user");
        const user = raw ? JSON.parse(raw) : null;
        const headers = {};
        if (user?.id) headers["X-Audit-User-Id"] = String(user.id);
        if (user?.username) headers["X-Audit-Username"] = String(user.username);
        return headers;
    } catch {
        return {};
    }
}

function toFormData(payload, files) {
    // Comments in English: Build multipart/form-data with fields + optional images.
    const fd = new FormData();

    Object.entries(payload || {}).forEach(([k, v]) => {
        if (v === undefined) return;
        if (v === null) {
            fd.append(k, "");
            return;
        }
        if (Array.isArray(v)) {
            fd.append(k, JSON.stringify(v));
            return;
        }
        if (typeof v === "object") {
            fd.append(k, JSON.stringify(v));
            return;
        }
        fd.append(k, String(v));
    });

    if (files?.logo) fd.append("company_logo", files.logo);
    if (files?.removeLogo) fd.append("remove_company_logo", "true");
    if (files?.startImage) {
        fd.append("start_image", files.startImage);
    }

    if (files?.startImageCrop) {
        fd.append("start_image_crop_x", String(files.startImageCrop.x ?? 50));
        fd.append("start_image_crop_y", String(files.startImageCrop.y ?? 50));
        fd.append("start_image_crop_zoom", String(files.startImageCrop.zoom ?? 1));
        if (files.startImageCrop.aspect !== undefined && files.startImageCrop.aspect !== null) {
            fd.append("start_image_crop_aspect", String(files.startImageCrop.aspect));
        }
    }

    if (Array.isArray(files?.images)) {
        files.images.forEach((f) => fd.append("images", f));
    }

    return fd;
}

export default function useCompanies() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchCompanies = useCallback(async (opts = {}) => {
        setLoading(true);
        setError("");
        try {
            const params = {};
            if (typeof opts.includeInactive === "boolean") {
                params.include_inactive = opts.includeInactive;
            }

            const res = await axios.get(`${API_BASE}/api/companies`, {
                withCredentials: true,
                params,
            });

            setCompanies(res.data || []);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const getCompanyById = useCallback(async (id) => {
        const res = await axios.get(`${API_BASE}/api/companies/${id}`, { withCredentials: true });
        return res.data;
    }, []);

    const trackCompanyView = useCallback(async (id) => {
        const res = await axios.post(`${API_BASE}/api/companies/${id}/views`, {}, { withCredentials: true });
        return res.data;
    }, []);

    const getCompanyStats = useCallback(async (id) => {
        const res = await axios.get(`${API_BASE}/api/companies/${id}/stats`, { withCredentials: true });
        return res.data;
    }, []);

    const getCompanyHistory = useCallback(async (id) => {
        const res = await axios.get(`${API_BASE}/api/companies/${id}/history`, { withCredentials: true });
        return res.data;
    }, []);

    const acquireCompanyEditLock = useCallback(async (id, lockToken, force = false) => {
        const res = await axios.post(
            `${API_BASE}/api/companies/${id}/edit-lock`,
            { lock_token: lockToken, force: !!force },
            { withCredentials: true, headers: getAuditHeaders() }
        );
        return res.data;
    }, []);

    const refreshCompanyEditLock = useCallback(async (id, lockToken) => {
        const res = await axios.post(
            `${API_BASE}/api/companies/${id}/edit-lock/heartbeat`,
            { lock_token: lockToken },
            { withCredentials: true, headers: getAuditHeaders() }
        );
        return res.data;
    }, []);

    const releaseCompanyEditLock = useCallback(async (id, lockToken) => {
        const res = await axios.delete(
            `${API_BASE}/api/companies/${id}/edit-lock`,
            {
                withCredentials: true,
                headers: getAuditHeaders(),
                data: { lock_token: lockToken },
            }
        );
        return res.data;
    }, []);

    const createCompany = useCallback(
        async (data, files = null) => {
            let res;
            if (files?.logo || files?.removeLogo || files?.startImage || (Array.isArray(files?.images) && files.images.length)) {
                const fd = toFormData(data, files);
                res = await axios.post(`${API_BASE}/api/companies`, fd, {
                    withCredentials: true,
                    headers: getAuditHeaders(),
                });
            } else {
                res = await axios.post(`${API_BASE}/api/companies`, data, { withCredentials: true, headers: getAuditHeaders() });
            }

            await fetchCompanies();
            return res.data.company;
        },
        [fetchCompanies]
    );

    const updateCompany = useCallback(
        async (id, data, files = null, editLockToken = "") => {
            const headers = {
                ...getAuditHeaders(),
                ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
            };
            let res;
            if (files?.logo || files?.removeLogo || files?.startImage || (Array.isArray(files?.images) && files.images.length)) {
                const fd = toFormData(data, files);
                res = await axios.put(`${API_BASE}/api/companies/${id}`, fd, {
                    withCredentials: true,
                    headers,
                });
            } else {
                res = await axios.put(`${API_BASE}/api/companies/${id}`, data, { withCredentials: true, headers });
            }

            await fetchCompanies();
            return res.data.company;
        },
        [fetchCompanies]
    );

    const deleteCompany = useCallback(
        async (id) => {
            await axios.delete(`${API_BASE}/api/companies/${id}`, { withCredentials: true });
            await fetchCompanies();
        },
        [fetchCompanies]
    );

    const deleteCompanyImage = useCallback(
        async (companyId, imageId, editLockToken = "") => {
            await axios.delete(`${API_BASE}/api/companies/${companyId}/images/${imageId}`, {
                withCredentials: true,
                headers: {
                    ...getAuditHeaders(),
                    ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                },
            });
            await fetchCompanies();
        },
        [fetchCompanies]
    );

    const setCompanyStartImage = useCallback(
        async (companyId, imageId, crop = null, editLockToken = "") => {
            const payload = { image_id: imageId };
            if (crop) {
                payload.crop_x = crop.x ?? 50;
                payload.crop_y = crop.y ?? 50;
                payload.crop_zoom = crop.zoom ?? 1;
                payload.crop_aspect = crop.aspect ?? null;
            }
            await axios.put(
                `${API_BASE}/api/companies/${companyId}/start-image`,
                payload,
                {
                    withCredentials: true,
                    headers: {
                        ...getAuditHeaders(),
                        ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                    },
                }
            );
            await fetchCompanies();
        },
        [fetchCompanies]
    );

    const updateCompanyImageCrop = useCallback(async (companyId, imageId, crop, editLockToken = "") => {
        const res = await axios.put(
            `${API_BASE}/api/companies/${companyId}/images/${imageId}/crop`,
            {
                crop_x: crop?.x ?? 50,
                crop_y: crop?.y ?? 50,
                crop_zoom: crop?.zoom ?? 1,
                crop_aspect: crop?.aspect ?? null,
            },
            {
                withCredentials: true,
                headers: {
                    ...getAuditHeaders(),
                    ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                },
            }
        );
        await fetchCompanies();
        return res.data.company;
    }, [fetchCompanies]);

    // NEW: persist image order
    const sortCompanyImages = useCallback(async (companyId, orderedIds, editLockToken = "") => {
        const res = await axios.put(
            `${API_BASE}/api/companies/${companyId}/images/sort`,
            { ordered_ids: orderedIds },
            {
                withCredentials: true,
                headers: {
                    ...getAuditHeaders(),
                    ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                },
            }
        );
        return res.data; // updated company payload
    }, []);

    const uploadCompanyMenu = useCallback(async (companyId, menuType, file, editLockToken = "") => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(
            `${API_BASE}/api/companies/${companyId}/menus/${menuType}`,
            fd,
            {
                withCredentials: true,
                headers: {
                    ...getAuditHeaders(),
                    ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                },
            }
        );
        await fetchCompanies();
        return res.data.company;
    }, [fetchCompanies]);

    const deleteCompanyMenu = useCallback(async (companyId, menuType, editLockToken = "") => {
        const res = await axios.delete(
            `${API_BASE}/api/companies/${companyId}/menus/${menuType}`,
            {
                withCredentials: true,
                headers: {
                    ...getAuditHeaders(),
                    ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                },
            }
        );
        await fetchCompanies();
        return res.data.company;
    }, [fetchCompanies]);

    const setCompanyMenuUrl = useCallback(async (companyId, menuType, externalUrl, editLockToken = "") => {
        const res = await axios.put(
            `${API_BASE}/api/companies/${companyId}/menus/${menuType}/url`,
            { external_url: externalUrl || "" },
            {
                withCredentials: true,
                headers: {
                    ...getAuditHeaders(),
                    ...(editLockToken ? { "X-Company-Edit-Lock-Token": editLockToken } : {}),
                },
            }
        );
        await fetchCompanies();
        return res.data.company;
    }, [fetchCompanies]);

    const setCompanyActiveSafe = useCallback(
        async (id, isActive) => {
            const res = await axios.put(`${API_BASE}/api/companies/${id}/active`, { is_active: !!isActive }, { withCredentials: true, headers: getAuditHeaders() });
            await fetchCompanies();
            return res.data.company;
        },
        [fetchCompanies]
    );

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    return {
        companies,
        loading,
        error,
        fetchCompanies,
        getCompanyById,
        trackCompanyView,
        getCompanyStats,
        getCompanyHistory,
        acquireCompanyEditLock,
        refreshCompanyEditLock,
        releaseCompanyEditLock,
        createCompany,
        updateCompany,
        deleteCompany,
        deleteCompanyImage,
        setCompanyStartImage,
        updateCompanyImageCrop,
        sortCompanyImages,
        uploadCompanyMenu,
        deleteCompanyMenu,
        setCompanyMenuUrl,
        setCompanyActive: setCompanyActiveSafe,
    };
}
